import type { AppData } from '@hbac/contract';

/**
 * The app data (members, banks, settings) kept for the tab's life, with when it
 * was read and for whom (Plan AU 1).
 *
 * Signing in already returns it, so the first screen does not ask again; a
 * reload shows this copy at once while it is read again behind it.
 *
 * sessionStorage, not localStorage, on purpose: member emails and bank numbers
 * go when the tab closes, are cleared on sign-out, and a copy made for one
 * member is never shown to another.
 */

const KEY = 'hbac-appdata';

export interface CachedAppData {
  data: AppData;
  /** When it was read, epoch ms. */
  at: number;
  memberId: string;
}

export function parseCachedAppData(raw: string | null, memberId: string): CachedAppData | null {
  if (!raw || !memberId) return null;
  try {
    const c = JSON.parse(raw) as Partial<CachedAppData>;
    const d = c?.data;
    if (c?.memberId !== memberId || typeof c.at !== 'number' || !d || !Array.isArray(d.members) || !Array.isArray(d.banks)) return null;
    return { data: d, at: c.at, memberId };
  } catch {
    // Corrupt entry: a cache miss.
    return null;
  }
}

export function readCachedAppData(memberId: string): CachedAppData | null {
  try {
    return parseCachedAppData(sessionStorage.getItem(KEY), memberId);
  } catch {
    return null;
  }
}

export function writeCachedAppData(data: AppData | null | undefined, memberId: string, at = Date.now()): void {
  try {
    if (data && memberId) sessionStorage.setItem(KEY, JSON.stringify({ data, at, memberId } satisfies CachedAppData));
  } catch {
    // Quota or blocked storage: the cache is an optimisation, never required.
  }
}

export function clearCachedAppData(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to do: worst case a stale entry goes with the tab.
  }
}
